// ---------------------------------------------------------------------------
// The one creator-facing presentation of a failed .fig import.
//
// UX audit (2026-07-27) findings 1 + 3: the failure was either invisible (the
// creator was navigated to a blank canvas) or shown as a raw upstream body
// with circular guidance. This component always answers the three questions —
// what failed, what it means, one honest next action — and NEVER renders the
// retained transport text unless the developer detail toggle is on.
// ---------------------------------------------------------------------------

import { AlertTriangle } from "lucide-react";
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import {
  getOpenPencilImportFailureCopy,
} from "./openpencil-import-failure-copy";
import type { OpenPencilImportFailureReason } from "./openpencil-import-failure";

const TECHNICAL_DETAILS_LABEL = {
  en: "Technical details",
  ko: "기술 세부 정보",
  ja: "技術的な詳細",
  zh: "技术详情",
  "zh-TW": "技術詳細資訊",
} satisfies Record<LocaleCode, string>;

interface Props {
  reason: OpenPencilImportFailureReason;
  /** The picked file, so the creator knows exactly which attempt failed. */
  fileName?: string | null;
  /** Retained upstream text — rendered only with showDeveloperDetails. */
  detail?: string | null;
  showDeveloperDetails?: boolean;
  onRetry?: () => void;
  retryLabel?: string;
  testId?: string;
}

export function OpenPencilImportFailureNotice({
  reason,
  fileName,
  detail,
  showDeveloperDetails = false,
  onRetry,
  retryLabel,
  testId = "openpencil-import-failure",
}: Props) {
  const { locale } = useStudioLocale();
  const copy = getOpenPencilImportFailureCopy(locale, reason);
  const showRetry = Boolean(onRetry && copy.retryable);

  return (
    <section
      className="rounded-lg border border-destructive/40 bg-destructive/5 p-4"
      role="alert"
      data-testid={testId}
      data-import-failure-reason={reason}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <h3
            className="text-sm font-semibold text-foreground"
            data-testid={`${testId}-title`}
          >
            {copy.title}
          </h3>
          {fileName ? (
            <p
              className="mt-1 truncate text-xs text-muted-foreground"
              data-testid={`${testId}-file`}
            >
              {fileName}
            </p>
          ) : null}
          <p
            className="mt-2 text-sm leading-6 text-muted-foreground"
            data-testid={`${testId}-meaning`}
          >
            {copy.meaning}
          </p>
          {!showRetry ? (
            <p
              className="mt-2 text-sm font-medium leading-6 text-foreground"
              data-testid={`${testId}-next-action`}
            >
              {copy.nextAction}
            </p>
          ) : null}
          {/* A retry affordance is offered ONLY when repeating the same
              action can plausibly work — never as decoration. */}
          {showRetry ? (
            <button
              type="button"
              onClick={onRetry!}
              data-testid={`${testId}-retry`}
              className="mt-3 inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
            >
              {retryLabel ?? copy.nextAction}
            </button>
          ) : null}
          {showDeveloperDetails && detail ? (
            <details className="group mt-3 text-xs text-muted-foreground">
              <summary className="cursor-pointer font-medium text-muted-foreground">
                {TECHNICAL_DETAILS_LABEL[locale]}
              </summary>
              <pre
                className="mt-2 max-h-24 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground"
                data-testid={`${testId}-detail`}
              >
                {detail}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    </section>
  );
}
